import moment from 'moment';
import {
  JIRA_LINK,
  JIRA_CORP_LINK,
  JIRA_ERROR_MESSAGE,
  JIRA_ISSUE_ID
} from 'utils/constants';
import { validateJiraLink } from '../SlideOutPanel/utils/phasedFieldUpdateUtils';
import {
  milestoneLinkAllowlist,
  matchesAdobeSharePointMilestonePath
} from '../../../config/milestoneLinkAllowlist';

export const simpleMemoize = <T>(fn: (arg: T) => any) => {
  let lastArg: T;
  let lastResult: any;
  return (arg: T) => {
    if (arg !== lastArg) {
      lastArg = arg;
      lastResult = fn(arg);
    }
    return lastResult;
  };
};

export const required = (value: any): string | undefined =>
  [null, undefined].includes(value) ? 'Required' : undefined;

const isJiralink = (
  value: string
): {
  hostname: string;
  pathname: string;
} => {
  try {
    const url = new URL(value);
    const hostname = url.hostname;
    const pathname = url.pathname;
    if (
      !JIRA_CORP_LINK.some(
        item => item.toLowerCase() === hostname.toLowerCase()
      )
    ) {
      return { hostname: '', pathname: '' };
    }
    if (!JIRA_ISSUE_ID[hostname].test(pathname.toLowerCase())) {
      return { hostname: hostname, pathname: '' };
    }
    return { hostname: hostname, pathname: pathname };
  } catch (error) {
    console.log('Invalid url');
    return { hostname: '', pathname: '' };
  }
};

const isValidJiralink = (
  value: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> => validateJiraLink(value, onSuccess, onError);

export const mustBeValidatedWrapper = async (
  value: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
) => {
  if (!value) return;

  // Check if the input matches shorthand format (e.g., ADR-123)
  if (JIRA_LINK.some(item => item.test(value))) {
    try {
      await isValidJiralink(value, onSuccess, onError);
      return undefined;
    } catch (error) {
      console.log('Enter a valid Jira URL', error);
      if (onError) onError(error);
      return JIRA_ERROR_MESSAGE;
    }
  }

  const { hostname, pathname } = isJiralink(value);
  if (hostname && pathname) {
    try {
      const issueId = pathname.split('/');
      if (
        (issueId?.length === 4 && issueId[issueId.length - 1] !== '') ||
        issueId?.length !== 3
      ) {
        throw new Error('Invalid URL');
      }

      // if user added the link as https://jira.corp.adobe.com/browse/ADR-1297/ [: generally when copypasting. so ignore the last character]
      const curIssueId =
        issueId[issueId.length - 1] == ''
          ? issueId[issueId.length - 2]
          : issueId[issueId.length - 1];

      if (!JIRA_ISSUE_ID[hostname].test(curIssueId)) {
        isValidJiralink(curIssueId, onSuccess, onError);
      }
      return undefined;
    } catch (error) {
      console.log('Enter a valid Jira URL', error);
      if (onError) onError(error);
      return JIRA_ERROR_MESSAGE;
    }
  }
  if (hostname) {
    return JIRA_ERROR_MESSAGE;
  }
  if (pathname) {
    return JIRA_ERROR_MESSAGE;
  }
  return JIRA_ERROR_MESSAGE;
};

const urlRegex =
  /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(:\d+)?(\/[\w\-.~:/?#\[\]@!$&'()*+,;=%]*)?$/i;

export const mustBeValidUrl = (value: string): string | undefined => {
  if (!value) return undefined;

  const trimmedValue = value.trim();

  // Validate using regex pattern
  if (urlRegex.test(trimmedValue)) {
    return undefined;
  }

  return 'Please enter a valid URL (e.g., https://example.com or www.example.com)';
};

/** Error message for invalid milestone/demo link domains (copy is maintained in config). */
export const MILESTONE_LINK_ERROR_MESSAGE =
  milestoneLinkAllowlist.userFacingErrorMessage;

/** Parse URL with optional https prefix; returns null if invalid */
const tryParseUrl = (value: string): URL | null => {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;
  const normalized = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    return new URL(normalized);
  } catch {
    return null;
  }
};

/**
 * Check if URL is from allowed demo sources: Frame.io (incl. f.io) or Figma.
 * Uses hostname validation; only allows known subdomains to avoid malicious-site.frame.io.
 *
 * @param value - URL string to validate
 * @returns true if hostname is frame.io, next.frame.io, f.io, figma.com, www.figma.com, or app.figma.com
 */
export const isAllowedDemoUrl = (value: string): boolean => {
  const url = tryParseUrl(value);
  if (!url) return false;

  const hostname = url.hostname.toLowerCase();
  // Only known Frame.io hosts (not *.frame.io - avoids malicious-site.frame.io)
  const isFrameIo = hostname === 'frame.io' || hostname === 'next.frame.io';
  // f.io shortened links - exact match only
  const isShortFio = hostname === 'f.io';
  const isFigma =
    hostname === 'figma.com' ||
    hostname === 'www.figma.com' ||
    hostname === 'app.figma.com';

  return isFrameIo || isShortFio || isFigma;
};

/**
 * Check if URL is from allowed milestone sources: SharePoint (DMESLC + approved ops sites),
 * Adobe Experience, Frame.io (incl. f.io), or Figma.
 * Uses hostname/path validation to avoid false positives from substring matches.
 *
 * @param value - URL string to validate
 * @returns true if URL is from an allowed source
 */
export const isAllowedMilestoneUrl = (value: string): boolean => {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;

  // First, allow known demo sources (Frame.io / Figma)
  if (isAllowedDemoUrl(value)) return true;

  const url = tryParseUrl(value);
  if (!url) return false;

  const hostname = url.hostname.toLowerCase();
  const pathname = url.pathname.toLowerCase();

  // Adobe Experience: experience.adobe.com or subdomains
  if (
    hostname === 'experience.adobe.com' ||
    hostname.endsWith('.experience.adobe.com')
  ) {
    return true;
  }

  // SharePoint: allowed site path segments — see src/config/milestoneLinkAllowlist.ts
  if (hostname === milestoneLinkAllowlist.adobeSharePointHostname) {
    return matchesAdobeSharePointMilestonePath(pathname);
  }

  return false;
};

/** document_link only — SharePoint / Frame / Figma allowlist */
const MILESTONE_ALLOWLIST_LINK_FIELD_IDS = new Set(['document_link']);

/**
 * Validates a milestone link field by field ID.
 * document_link: URL format + domain allowlist.
 * meeting_link, recording_link, notes_link: URL format only.
 */
export const validateMilestoneLinkField = (
  fieldId: string,
  value: string
): string | undefined => {
  const basicUrlError = mustBeValidUrl(value);
  if (basicUrlError) return basicUrlError;

  if (MILESTONE_ALLOWLIST_LINK_FIELD_IDS.has(fieldId)) {
    if (!isAllowedMilestoneUrl(value)) {
      return MILESTONE_LINK_ERROR_MESSAGE;
    }
  }

  return undefined;
};

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const mustBeValidEmail = (value: string): string | undefined =>
  value
    ? emailRegex.test(value)
      ? undefined
      : 'Must be a valid email'
    : undefined;

export const mustBeValidEmailArray = (emails: string[]): string | undefined => {
  if (emails && emails.length) {
    const invalidEmails: string[] = [];
    emails.forEach(email => {
      if (!emailRegex.test(email)) invalidEmails.push(email);
    });
    if (invalidEmails.length)
      return `Invalid Email${
        invalidEmails.length > 1 ? 's' : ''
      } - ${invalidEmails.join(', ')}`;
    return undefined;
  }
  return 'Must add at least 1 email';
};

const alphabetRegex = /[a-zA-Z]/;
export const initialAlphabet = (value: string): string | undefined =>
  value
    ? alphabetRegex.test(value.charAt(0))
      ? undefined
      : 'Must start with an alphabet'
    : undefined;

export const mustMatchRegex =
  (regex: RegExp, errorMessage: string) =>
  (value: string): string | undefined =>
    value ? (regex.test(value) ? undefined : errorMessage) : undefined;

const simpleTextRegex = /^[\w\-\s]+$/;
export const mustBeSimpleText = (value: string): string | undefined =>
  value
    ? simpleTextRegex.test(value)
      ? undefined
      : 'Must contain only alphanumeric, -, _ and space characters'
    : undefined;

export const mustBeUnique =
  (existingValues: string[]) =>
  (value: string): string | undefined =>
    existingValues.includes(value) ? 'Must be unique' : undefined;

export const requiredArray = (value: any[]): string | undefined =>
  value && value.length > 0 ? undefined : 'Must select at least 1 item';

export const requiredAdUsers = (value: any[]): string | undefined =>
  value && value.length > 0 ? undefined : 'Must add at least 1 user';

export const requiredSingleUser = (value: any[]): string | undefined =>
  value && value.length > 0 ? undefined : 'Must add 1 user';

export const mustBeNumber = (value: any): string | undefined =>
  isNaN(value) ? 'Must be a number' : undefined;

export const minValue =
  (min: number) =>
  (value: number): string | undefined =>
    value >= min ? undefined : `Must be a minimum of ${min}`;

export const maxValue =
  (max: number) =>
  (value: number): string | undefined =>
    value <= max ? undefined : `Must be a maximum of ${max}`;

export const minValueAgsId = (value: number): string | undefined =>
  value >= 1 ? undefined : 'Must be a minimum of 001';

export const mustBeValidDateFormat = (value: string): string | undefined =>
  value
    ? moment(value, 'YYYY-MM-DD', true).isValid()
      ? undefined
      : 'Invalid date format'
    : undefined;

export const mustBeMinDate =
  (min: moment.Moment) =>
  (value: moment.Moment): string | undefined => {
    if (value) {
      const minDate = moment(min).format('MMM DD, YYYY');
      return moment(value).isSameOrAfter(min)
        ? undefined
        : `Must be on or after ${minDate}`;
    }
    return undefined;
  };

export const mustBeMaxDate =
  (max: moment.Moment) =>
  (value: moment.Moment): string | undefined => {
    if (value) {
      const maxDate = moment(max).format('MMM DD, YYYY');
      return moment(value).isSameOrBefore(max)
        ? undefined
        : `Must be on or before ${maxDate}`;
    }
    return undefined;
  };

export const mustBeOfLengthRange =
  (min: number, max: number) =>
  (value: string): string | undefined =>
    value
      ? value.length >= min && value.length <= max
        ? undefined
        : `Must be ${min} to ${max} characters long`
      : undefined;

export const mustEndWith =
  (str: string) =>
  (value: string): string | undefined =>
    value
      ? value.endsWith(str)
        ? undefined
        : `Must end with ${str}`
      : undefined;

export const composeValidators =
  (
    validators: ((value: any, name?: string) => string | undefined)[] = [],
    name?: string
  ) =>
  (value: any): string | undefined => {
    const errors = validators
      .map(validator => validator && validator(value, name))
      .filter(Boolean);
    return errors.length > 0 ? errors[0] : undefined;
  };
